import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, message, notification, Divider, Modal, Space, Typography } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import './login.css';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { EMAIL_VERIFICATION_REQUIRED, RequestCheckAuth, RequestLogin } from '../../Actions/user.action';
import { TypeAnimation } from 'react-type-animation';
import { useTranslation } from 'react-i18next';
import { getAccessToken } from '../../utils/accessToken';
import { isCookieAuthMode } from '../../utils/authMode';
import { createCheckoutSession, getBillingErrorMessage } from '../../Services/billing.service';
import { resend_email_verification } from '../../Services/user.service';
import {
    clearPendingBillingPlan,
    getPendingBillingPlan,
    isPaidPlan,
    normalizePublicPlan,
    savePendingBillingPlan,
} from '../../Services/pendingBillingPlan.service';

const { Text } = Typography;

function LoginForm() {
    const { t } = useTranslation('login');

    const getLandingPageByRole = useCallback((user) => {
        const role = user?.role;
        if (role === "SUPER_ADMIN") return "/workspace";
        if (role === "ADMIN_CLIENT" || role === "USER") return "/workspace";
        return "/workspace";
    }, []);

    const openNotification = () => {
        notification.open({
            message: t('notifications.coming_soon_title'),
            description: t('notifications.coming_soon_description'),
            placement: "topRight",
            duration: 3,
            style: {
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(11, 61, 145, 0.15)',
                fontFamily: 'Work Sans, sans-serif'
            }
        });
    };

    const [loading, setLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutPromptPlan, setCheckoutPromptPlan] = useState(null);
    const [authenticatedUser, setAuthenticatedUser] = useState(null);
    const [transitioning, setTransitioning] = useState(false);
    const [emailVerificationEmail, setEmailVerificationEmail] = useState("");
    const [resendLoading, setResendLoading] = useState(false);
    const [resendStatus, setResendStatus] = useState("idle");
    const [resendError, setResendError] = useState("");
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const timerRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const requestedPlan = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.has("plan") ? normalizePublicPlan(params.get("plan")) : null;
    }, [location.search]);

    // Premium white fade handoff: covers the viewport in pure white,
    // plants a sessionStorage flag for the workspace to trigger the
    // reverse dissolve, then navigates after the veil is fully opaque.
    const handoffToWorkspace = useCallback((user) => {
        try { sessionStorage.setItem('verdictia_auth_fade', '1'); } catch (e) { /* noop */ }
        setTransitioning(true);
        timerRef.current = window.setTimeout(() => {
            navigate(getLandingPageByRole(user));
        }, 650);
    }, [navigate, getLandingPageByRole]);

    const handoffAfterAuth = useCallback(async (user) => {
        const pendingPlan = getPendingBillingPlan();

        if (pendingPlan === "trial") {
            clearPendingBillingPlan();
            handoffToWorkspace(user);
            return;
        }

        if (!isPaidPlan(pendingPlan)) {
            handoffToWorkspace(user);
            return;
        }

        setAuthenticatedUser(user);
        setCheckoutPromptPlan(pendingPlan);
    }, [handoffToWorkspace]);

    const continueWithTrial = useCallback(() => {
        clearPendingBillingPlan();
        setCheckoutPromptPlan(null);
        handoffToWorkspace(authenticatedUser);
    }, [authenticatedUser, handoffToWorkspace]);

    const continueToCheckout = useCallback(async () => {
        if (!isPaidPlan(checkoutPromptPlan)) {
            continueWithTrial();
            return;
        }

        setCheckoutLoading(true);
        try {
            await createCheckoutSession(checkoutPromptPlan, { source: "register_pending_plan" });
            clearPendingBillingPlan();
        } catch (error) {
            message.error(getBillingErrorMessage(error, t("checkout.launchError")));
            setCheckoutLoading(false);
        }
    }, [checkoutPromptPlan, continueWithTrial, t]);

    const clearEmailVerificationNotice = useCallback(() => {
        const shouldReset = Boolean(emailVerificationEmail) || Boolean(resendError) || resendStatus !== "idle";
        if (!shouldReset) return;

        setEmailVerificationEmail("");
        setResendStatus("idle");
        setResendError("");
    }, [emailVerificationEmail, resendError, resendStatus]);

    const handleInputFocus = useCallback(() => {
        document.body?.classList.add("login-performance-mode");
    }, []);

    const handleResendVerification = useCallback(async () => {
        if (!emailVerificationEmail) return;

        setResendLoading(true);
        setResendStatus("idle");
        setResendError("");
        try {
            await resend_email_verification(emailVerificationEmail);
            setResendStatus("success");
        } catch (error) {
            if (error?.response?.status === 429) {
                setResendError(t("emailVerification.resendRateLimited"));
            } else {
                setResendError(t("emailVerification.resendError"));
            }
        } finally {
            setResendLoading(false);
        }
    }, [emailVerificationEmail, t]);

    useEffect(() => {
        return () => {
            document.body?.classList.remove("login-performance-mode");
        };
    }, []);

    useEffect(() => {
        return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.has("plan")) {
            savePendingBillingPlan(requestedPlan);
        }
    }, [location.search, requestedPlan]);

    useEffect(() => {
        const token = getAccessToken();
        if (token || isCookieAuthMode()) {
            dispatch(RequestCheckAuth(navigate, handoffAfterAuth));
        }
    }, [dispatch, navigate, handoffAfterAuth]);

    const handleLogin = () => {
        const email = String(usernameRef.current?.input?.value || usernameRef.current?.value || "").trim().toLowerCase();
        const password = String(passwordRef.current?.input?.value || passwordRef.current?.value || "");

        clearEmailVerificationNotice();

        if (!email || !password) {
            message.error(t('validation.both_required'), 3);
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            message.error(t('validation.invalid_email'), 3);
            return;
        }

        setLoading(true);

        Promise.resolve(dispatch(RequestLogin(email, password, (user) => {
            handoffAfterAuth(user);
        })))
            .then((result) => {
                if (result?.code === EMAIL_VERIFICATION_REQUIRED) {
                    setEmailVerificationEmail(email);
                    setResendStatus("idle");
                    setResendError("");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const checkoutPromptPlanLabel = String(checkoutPromptPlan || "").toUpperCase();

    return (
        <div className="form-content">
            {/* Auth → workspace premium white dissolve veil.
                Rendered via portal directly into document.body to escape any
                transform / filter stacking context on ancestor elements. */}
            {createPortal(
                <div
                    className={`auth-fade-veil${transitioning ? ' is-active' : ''}`}
                    aria-hidden="true"
                />,
                document.body
            )}
            <h2><b>{t('form.welcome')}</b></h2>

            <TypeAnimation
                sequence={[t('form.typewriter'), 2000]}
                wrapper="span"
                speed={50}
                repeat={Infinity}
                className="typing-animation"
            />



            <div className="input-structure">
                <Input
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder={t('form.username_placeholder')}
                    ref={usernameRef}
                    onChange={clearEmailVerificationNotice}
                    onFocus={handleInputFocus}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                    disabled={loading || checkoutLoading || Boolean(checkoutPromptPlan)}
                />
            </div>

            <div className="input-structure">
                <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder={t('form.password_placeholder')}
                    ref={passwordRef}
                    onChange={clearEmailVerificationNotice}
                    onFocus={handleInputFocus}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                    disabled={loading || checkoutLoading || Boolean(checkoutPromptPlan)}
                />
            </div>

            {emailVerificationEmail && (
                <div className="login-verification-panel" role="alert" aria-live="polite">
                    <SafetyCertificateOutlined aria-hidden="true" />
                    <div className="login-verification-copy">
                        <strong>{t("emailVerification.requiredTitle")}</strong>
                        <p>{t("emailVerification.requiredMessage")}</p>
                        <Button
                            type="default"
                            size="small"
                            onClick={handleResendVerification}
                            loading={resendLoading}
                            disabled={resendLoading}
                        >
                            {t("emailVerification.resendCta")}
                        </Button>
                        {resendStatus === "success" && (
                            <p className="login-verification-status" role="status">
                                {t("emailVerification.resendGenericSuccess")}
                            </p>
                        )}
                        {resendError && (
                            <p className="login-verification-error" role="alert">
                                {resendError}
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', marginTop: '-12px' }}>
                <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    style={{
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                        fontSize: '13px',
                        color: '#667085',
                        cursor: 'pointer',
                        fontFamily: 'inherit'
                    }}
                >
                    {t('form.forgot_password')}
                </button>
            </div>

            <Button
                className="login-button"
                size="large"
                type="primary"
                onClick={handleLogin}
                loading={loading || checkoutLoading}
            >
                {checkoutLoading ? t("checkout.opening") : t('form.submit')}
            </Button>

            <div className="register-entry">
                <span>{t('form.no_account')}</span>
                <button
                    type="button"
                    onClick={() => navigate(requestedPlan ? `/register?plan=${requestedPlan}` : '/register')}
                    disabled={loading || checkoutLoading || Boolean(checkoutPromptPlan)}
                >
                    {t('form.create_account')}
                </button>
            </div>

            <Divider>{t('form.or')}</Divider>

            <div className="sso-container">
                <a
                    href="#"
                    className="sso-button"
                    onClick={(e) => {
                        e.preventDefault();
                        openNotification();
                    }}
                    aria-label={t('sso.google')}
                >
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/250px-Google_%22G%22_logo.svg.png"
                        alt="Google"
                    />
                    <span>{t('sso.google')}</span>
                </a>

                <a
                    href="#"
                    className="sso-button"
                    onClick={(e) => {
                        e.preventDefault();
                        openNotification();
                    }}
                    aria-label={t('sso.microsoft')}
                >
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                        alt="Microsoft"
                    />
                    <span>{t('sso.microsoft')}</span>
                </a>

                <a
                    href="#"
                    className="sso-button"
                    onClick={(e) => {
                        e.preventDefault();
                        openNotification();
                    }}
                    aria-label={t('sso.apple')}
                >
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
                        alt="Apple"
                    />
                    <span>{t('sso.apple')}</span>
                </a>
            </div>

            <Modal
                open={Boolean(checkoutPromptPlan)}
                title={t("checkout.promptTitle")}
                centered
                onCancel={continueWithTrial}
                footer={null}
                maskClosable={!checkoutLoading}
                closable={!checkoutLoading}
            >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Text>
                        {t("checkout.promptBody", { plan: checkoutPromptPlanLabel })}
                    </Text>
                    <Space wrap>
                        <Button
                            type="primary"
                            onClick={continueToCheckout}
                            loading={checkoutLoading}
                        >
                            {t("checkout.continueToPayment")}
                        </Button>
                        <Button onClick={continueWithTrial} disabled={checkoutLoading}>
                            {t("checkout.startTrialLater")}
                        </Button>
                    </Space>
                </Space>
            </Modal>
        </div>
    );
}

export default LoginForm;
